import type { DurationHistogramBin } from './weldDashboardMetrics';
import type { NormalizedWeld } from './weldDashboardMetrics';
import { DASHBOARD_COLORS } from '../components/dashboard/dashboardTokens';

export const QUALITY_THRESHOLDS = {
  acceptable: 95,
  warning: 90,
} as const;

export function qualityScoreStatus(score: number): 'good' | 'warning' | 'critical' {
  if (score >= QUALITY_THRESHOLDS.acceptable) return 'good';
  if (score >= QUALITY_THRESHOLDS.warning) return 'warning';
  return 'critical';
}

export function qualityScoreStatusLabel(score: number): string {
  const status = qualityScoreStatus(score);
  if (status === 'good') return 'Good';
  if (status === 'warning') return 'Warning';
  return 'Critical';
}

export type DurationStatus = 'within' | 'warning' | 'outlier';

export interface DurationScatterPoint {
  sequence: number;
  weldNumber: string;
  jobNumber: string;
  durationSec: number;
  durationMin: number;
  status: DurationStatus;
  targetDeviationSec: number;
  targetDeviationMin: number;
  trendMin: number | null;
  timestampMs: number | null;
  machine: string | null;
  operator: string | null;
  shift: string | null;
  recipe: string | null;
}

export interface DurationChartStats {
  avgDurationSec: number;
  fastestSec: number;
  slowestSec: number;
  outlierCount: number;
  targetMinSec: number;
  targetMaxSec: number;
  yMin: number;
  yMax: number;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function meanAndStd(values: number[]): { mean: number; std: number } {
  if (values.length === 0) return { mean: 0, std: 0 };
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return { mean, std: Math.sqrt(variance) };
}

function classifyDuration(
  durationSec: number,
  targetMinSec: number,
  targetMaxSec: number,
  mean: number,
  std: number,
): DurationStatus {
  const bandWidth = Math.max(targetMaxSec - targetMinSec, mean * 0.05);
  const warningMargin = Math.max(std, bandWidth * 0.35);
  const outlierHigh = targetMaxSec + warningMargin;
  const outlierLow = Math.max(0, targetMinSec - warningMargin);

  if (durationSec > outlierHigh || durationSec < outlierLow) return 'outlier';
  if (durationSec > targetMaxSec || durationSec < targetMinSec) return 'warning';
  return 'within';
}

function rollingTrendMin(points: DurationScatterPoint[], window = 7): void {
  points.forEach((point, i) => {
    const slice = points.slice(Math.max(0, i - window + 1), i + 1);
    const avgSec = slice.reduce((s, p) => s + p.durationSec, 0) / slice.length;
    point.trendMin = avgSec / 60;
  });
}

export function buildDurationScatter(welds: NormalizedWeld[]): DurationScatterPoint[] {
  const withDuration = welds.filter((w) => w.durationSec != null && w.durationSec > 0);
  if (withDuration.length === 0) return [];

  const durations = withDuration.map((w) => w.durationSec!);
  const sorted = [...durations].sort((a, b) => a - b);
  const targetMinSec = percentile(sorted, 25);
  const targetMaxSec = percentile(sorted, 75);
  const bandCenter = (targetMinSec + targetMaxSec) / 2;
  const { mean, std } = meanAndStd(durations);

  const points: DurationScatterPoint[] = withDuration.map((w) => {
    const durationSec = w.durationSec!;
    const status = classifyDuration(durationSec, targetMinSec, targetMaxSec, mean, std);
    const targetDeviationSec = durationSec - bandCenter;

    return {
      sequence: w.sequence,
      weldNumber: w.weldNumber,
      jobNumber: w.jobNumber,
      durationSec,
      durationMin: durationSec / 60,
      status,
      targetDeviationSec,
      targetDeviationMin: targetDeviationSec / 60,
      trendMin: null,
      timestampMs: w.startTimeMs,
      machine: w.weldingSystem,
      operator: null,
      shift: w.shift,
      recipe: w.wpsNumber,
    };
  });

  rollingTrendMin(points);
  return points;
}

export function durationChartStats(points: DurationScatterPoint[]): DurationChartStats | null {
  if (points.length === 0) return null;

  const durations = points.map((p) => p.durationSec);
  const sorted = [...durations].sort((a, b) => a - b);
  const targetMinSec = percentile(sorted, 25);
  const targetMaxSec = percentile(sorted, 75);
  const mins = points.map((p) => p.durationMin);
  const padding = Math.max(0.5, (Math.max(...mins) - Math.min(...mins)) * 0.12);

  return {
    avgDurationSec: durations.reduce((s, v) => s + v, 0) / durations.length,
    fastestSec: Math.min(...durations),
    slowestSec: Math.max(...durations),
    outlierCount: points.filter((p) => p.status === 'outlier').length,
    targetMinSec,
    targetMaxSec,
    yMin: Math.max(0, Math.floor((Math.min(...mins) - padding) * 10) / 10),
    yMax: Math.ceil((Math.max(...mins) + padding) * 10) / 10,
  };
}

export function sampleSequenceTicks(
  points: DurationScatterPoint[],
  maxTicks = 8,
): { value: number; label: string }[] {
  if (points.length === 0) return [];
  if (points.length <= maxTicks) {
    return points.map((p) => ({ value: p.sequence, label: p.weldNumber }));
  }
  const step = Math.ceil(points.length / maxTicks);
  const ticks: { value: number; label: string }[] = [];
  for (let i = 0; i < points.length; i += step) {
    ticks.push({ value: points[i].sequence, label: points[i].weldNumber });
  }
  const last = points[points.length - 1];
  if (ticks[ticks.length - 1]?.value !== last.sequence) {
    ticks.push({ value: last.sequence, label: last.weldNumber });
  }
  return ticks;
}

export const DURATION_STATUS_COLOR: Record<DurationStatus, string> = {
  within: DASHBOARD_COLORS.green,
  warning: '#B38600',
  outlier: DASHBOARD_COLORS.readableRed,
};

const FIXED_DURATION_BIN_MINUTES = 5;

function formatFixedBinLabel(loMin: number, hiMin: number): string {
  return `${loMin}–${hiMin} min`;
}

/**
 * Build fixed 5-minute duration histogram bins from 0 through the slowest weld.
 * Empty buckets between first and last needed range are included.
 */
export function buildDynamicDurationBins(durationsSec: number[]): DurationHistogramBin[] {
  const valid = durationsSec.filter((d) => Number.isFinite(d) && d > 0);
  if (valid.length === 0) return [];

  const maxSec = Math.max(...valid);
  const maxMin = maxSec / 60;
  const endMin = Math.max(FIXED_DURATION_BIN_MINUTES, Math.ceil(maxMin / FIXED_DURATION_BIN_MINUTES) * FIXED_DURATION_BIN_MINUTES);

  const bins: DurationHistogramBin[] = [];
  for (let loMin = 0; loMin < endMin; loMin += FIXED_DURATION_BIN_MINUTES) {
    const hiMin = loMin + FIXED_DURATION_BIN_MINUTES;
    const loSec = loMin * 60;
    const hiSec = hiMin * 60;
    const isLast = hiMin >= endMin;
    const count = valid.filter((d) =>
      isLast ? d >= loSec && d <= maxSec + 0.001 : d >= loSec && d < hiSec,
    ).length;
    bins.push({
      label: formatFixedBinLabel(loMin, hiMin),
      count,
      loSec,
      hiSec,
    });
  }

  return bins;
}

export interface QualityChartStats {
  min: number;
  max: number;
  avg: number;
  yMin: number;
  yMax: number;
  belowThresholdCount: number;
  stabilityLabel: 'Stable' | 'Moderate' | 'Unstable';
  stabilityTone: 'green' | 'yellow' | 'red';
}

export interface QualityTrendRow {
  sequence: number;
  weldNumber: string;
  jobNumber: string;
  qualityScore: number;
  timestampMs: number | null;
  rollingAvg: number;
  isAbnormal: boolean;
  abnormalReason: string | null;
}

function qualityStability(scores: number[]): Pick<QualityChartStats, 'stabilityLabel' | 'stabilityTone'> {
  if (scores.length < 2) {
    return { stabilityLabel: 'Stable', stabilityTone: 'green' };
  }
  const mean = scores.reduce((s, v) => s + v, 0) / scores.length;
  const std = Math.sqrt(scores.reduce((s, v) => s + (v - mean) ** 2, 0) / scores.length);
  if (std <= 2) return { stabilityLabel: 'Stable', stabilityTone: 'green' };
  if (std <= 5) return { stabilityLabel: 'Moderate', stabilityTone: 'yellow' };
  return { stabilityLabel: 'Unstable', stabilityTone: 'red' };
}

export function enrichQualityTrend(
  data: {
    sequence: number;
    weldNumber: string;
    jobNumber?: string;
    qualityScore: number;
    timestampMs?: number | null;
  }[],
): { rows: QualityTrendRow[]; stats: QualityChartStats | null } {
  if (data.length === 0) return { rows: [], stats: null };

  const scores = data.map((d) => d.qualityScore);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const avg = scores.reduce((s, v) => s + v, 0) / scores.length;
  const span = max - min;
  const padding = span < 1 ? 2 : Math.max(2, span * 0.15);
  const stability = qualityStability(scores);

  const rows: QualityTrendRow[] = data.map((d, i) => {
    const window = data.slice(Math.max(0, i - 4), i + 1);
    const rollingAvg = window.reduce((s, w) => s + w.qualityScore, 0) / window.length;
    const prevRolling =
      i > 0
        ? data
            .slice(Math.max(0, i - 5), i)
            .reduce((s, w) => s + w.qualityScore, 0) / Math.min(5, i)
        : rollingAvg;
    const dropFromTrend = prevRolling - d.qualityScore;

    let isAbnormal = d.qualityScore < QUALITY_THRESHOLDS.acceptable;
    let abnormalReason: string | null = null;
    if (d.qualityScore < QUALITY_THRESHOLDS.warning) {
      abnormalReason = 'Critical quality drop';
      isAbnormal = true;
    } else if (d.qualityScore < QUALITY_THRESHOLDS.acceptable) {
      abnormalReason = 'Below acceptable threshold';
      isAbnormal = true;
    } else if (dropFromTrend >= 5) {
      isAbnormal = true;
      abnormalReason = 'Sudden quality drop';
    }

    return {
      ...d,
      jobNumber: d.jobNumber ?? '',
      timestampMs: d.timestampMs ?? null,
      rollingAvg,
      isAbnormal,
      abnormalReason,
    };
  });

  return {
    rows,
    stats: {
      min,
      max,
      avg,
      yMin: Math.max(0, Math.floor((min - padding) * 10) / 10),
      yMax: Math.min(100, Math.ceil((max + padding) * 10) / 10),
      belowThresholdCount: scores.filter((s) => s < QUALITY_THRESHOLDS.acceptable).length,
      ...stability,
    },
  };
}

export const ANALYTICS_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: `1px solid ${DASHBOARD_COLORS.border}`,
  fontSize: 12,
};

const SERIES_LABELS: Record<string, string> = {
  value: 'Count',
  count: 'Count',
  cumulativePct: 'Cumulative %',
  passRatePct: 'Pass rate',
  inspections: 'Inspections',
  defects: 'Defects',
  repairs: 'Repairs',
};

export function analyticsSeriesLabel(dataKey: string): string {
  return SERIES_LABELS[dataKey] ?? dataKey;
}

export function tooltipNumericValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function analyticsTooltipLabel(
  label: string | number | undefined,
  payload: { payload?: Record<string, unknown> }[] | undefined,
  nameKey = 'name',
): string {
  const row = payload?.[0]?.payload;
  if (row && typeof row[nameKey] === 'string') return row[nameKey] as string;
  if (row && typeof row.label === 'string') return row.label as string;
  if (row && typeof row.month === 'string') return row.month as string;
  return String(label ?? '');
}

/** Shared Recharts tooltip props for NDT QC charts. */
export const ndtChartTooltipProps = {
  contentStyle: ANALYTICS_TOOLTIP_STYLE,
  labelFormatter: (label: string, payload: { payload?: Record<string, unknown> }[]) =>
    analyticsTooltipLabel(label, payload),
  formatter: (value: number, name: string) => {
    if (name === 'cumulativePct') return [`${value.toFixed(1)}%`, 'Cumulative %'];
    if (typeof value === 'number' && String(name).includes('Pct')) {
      return [`${value.toFixed(1)}%`, analyticsSeriesLabel(name)];
    }
    return [String(value), analyticsSeriesLabel(name)];
  },
};
